// components/strategy/StrategyAuthGate.jsx — Phase 3B individual-account
// gate for the Stratégie workspace. Wraps PlanificationTab's content:
// renders the login/activation/MFA/recovery flow until a real,
// server-verified `admin_account` session with an active country
// context exists, then renders `children`. The legacy shared-password
// admin session (`require_admin`) is NEVER accepted here — see
// `strategyApi.js` (its own cookies/CSRF, its own 401 handling).
import React, { useEffect, useState, useCallback } from "react";
import { strategyAuth } from "../../lib/strategyAuth";
import { strategyEndpoints } from "../../lib/strategyApi";
import { StrategyLoginForm } from "./StrategyLoginForm";
import { StrategyActivateForm } from "./StrategyActivateForm";
import { StrategyMfaEnrollForm } from "./StrategyMfaEnrollForm";
import { Loader2 } from "lucide-react";

export const StrategyAuthGate = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [session, setSession] = useState(strategyAuth.get());
  const [view, setView] = useState("login"); // login | activate | mfa_enroll
  const [pendingMfaToken, setPendingMfaToken] = useState(null);

  useEffect(() => strategyAuth.subscribe(setSession), []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await strategyEndpoints.me();
      strategyAuth.setSession({
        account: data.account,
        memberships: data.memberships,
        activeCountryCode: data.active_country_code,
        activeRole: data.memberships.find((m) => m.country_code === data.active_country_code)?.role,
      });
    } catch {
      strategyAuth.clear();
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const onActivated = (mfaSetupToken) => {
    setPendingMfaToken(mfaSetupToken);
    setView("mfa_enroll");
  };

  const onMfaVerified = () => {
    setPendingMfaToken(null);
    setView("login");
  };

  const onLoggedIn = () => refreshMe();

  if (checking) {
    return (
      <div className="flex-1 flex items-center justify-center py-20" data-testid="strategy-auth-checking">
        <Loader2 className="w-6 h-6 animate-spin text-rescue-mute" />
      </div>
    );
  }

  if (session && session.activeCountryCode) {
    return <div data-testid="strategy-authenticated-shell">{children}</div>;
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center py-10 px-5" data-testid="strategy-auth-gate">
      <div className="w-full max-w-sm">
        <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-rescue-mute mb-4 text-center">
          Module Stratégie — compte administrateur individuel
        </div>
        {view === "login" && (
          <StrategyLoginForm
            onLoggedIn={onLoggedIn}
            onGoToActivate={() => setView("activate")}
          />
        )}
        {view === "activate" && (
          <StrategyActivateForm onActivated={onActivated} onCancel={() => setView("login")} />
        )}
        {view === "mfa_enroll" && pendingMfaToken && (
          <StrategyMfaEnrollForm mfaSetupToken={pendingMfaToken} onVerified={onMfaVerified} />
        )}
      </div>
    </div>
  );
};
